if (!customElements.get('sos-email-popup')) {
  const STORAGE_KEY = 'sos_email_popup';
  const TEASER_KEY = 'sos_email_popup_teaser';
  const SUBMIT_FLAG = 'sos_email_popup_submitted';
  const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const store = {
    get(storage, key) {
      try {
        return storage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (e) {}
    },
    remove(storage, key) {
      try {
        storage.removeItem(key);
      } catch (e) {}
    },
  };

  class SosEmailPopup extends HTMLElement {
    connectedCallback() {
      this.popup = this.querySelector('.sos-popup');
      this.dialog = this.querySelector('.sos-popup__dialog');
      this.teaser = this.querySelector('.sos-popup-teaser');
      this.form = this.querySelector('form');
      this.designMode = this.dataset.designMode === 'true';

      this.onKeydown = this.onKeydown.bind(this);
      this.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-popup-open]').forEach((el) => el.addEventListener('click', () => this.open()));
      this.querySelector('[data-teaser-dismiss]')?.addEventListener('click', () => this.dismissTeaser());
      this.querySelector('[data-popup-copy]')?.addEventListener('click', (e) => this.copyCode(e.currentTarget));
      this.form?.addEventListener('submit', () => store.set(sessionStorage, SUBMIT_FLAG, '1'));

      if (this.designMode) {
        this.setupDesignMode();
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('sos-popup-open');
    }

    init() {
      const submittedHere = store.get(sessionStorage, SUBMIT_FLAG) === '1';
      store.remove(sessionStorage, SUBMIT_FLAG);

      // Returning from a form post (Shopify reloads the page).
      if (this.querySelector('[data-popup-success]')) {
        this.setState('subscribed');
        if (submittedHere) this.open();
        return;
      }
      if (submittedHere && this.querySelector('[data-popup-error]')) {
        this.open();
        return;
      }

      if (this.dataset.hideForCustomer === 'true') return;

      const saved = this.getState();
      if (saved?.state === 'subscribed') return;

      if (saved?.state === 'closed') {
        const days = parseInt(this.dataset.reshowDays, 10) || 0;
        const reshowAt = saved.at + days * 864e5;
        if (days === 0 || Date.now() < reshowAt) {
          this.showTeaser();
          return;
        }
      }

      const delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
      this.timer = setTimeout(() => this.open(), delay);
    }

    setupDesignMode() {
      // In the theme editor, preview the popup while its section is selected.
      const section = this.closest('.shopify-section');
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target === section) this.open(false);
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target === section) this.close(false);
      });
    }

    getState() {
      try {
        return JSON.parse(store.get(localStorage, STORAGE_KEY));
      } catch (e) {
        return null;
      }
    }

    setState(state) {
      store.set(localStorage, STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    }

    open(trapFocus = true) {
      clearTimeout(this.timer);
      if (!this.popup || !this.popup.hidden) return;

      this.lastFocused = document.activeElement;
      this.popup.hidden = false;
      this.hideTeaser();
      document.documentElement.classList.add('sos-popup-open');
      document.addEventListener('keydown', this.onKeydown);
      this.popup.offsetHeight; // force reflow so the fade-in transition runs
      this.popup.classList.add('is-visible');

      if (trapFocus) {
        const input = this.dialog.querySelector('input[type="email"]');
        (input && window.matchMedia('(min-width: 750px)').matches ? input : this.dialog).focus({ preventScroll: true });
      }
    }

    close(remember = true) {
      if (!this.popup || this.popup.hidden) return;

      this.popup.classList.remove('is-visible');
      document.documentElement.classList.remove('sos-popup-open');
      document.removeEventListener('keydown', this.onKeydown);
      setTimeout(() => (this.popup.hidden = true), 250);
      this.lastFocused?.focus?.({ preventScroll: true });

      if (!remember) return;
      if (this.getState()?.state !== 'subscribed') {
        this.setState('closed');
        this.showTeaser();
      }
    }

    showTeaser() {
      if (!this.teaser || store.get(localStorage, TEASER_KEY) === 'hidden') return;
      this.teaser.hidden = false;
    }

    hideTeaser() {
      if (this.teaser) this.teaser.hidden = true;
    }

    dismissTeaser() {
      store.set(localStorage, TEASER_KEY, 'hidden');
      this.hideTeaser();
    }

    copyCode(button) {
      const code = this.querySelector('[data-popup-code]')?.textContent.trim();
      if (!code) return;

      const done = () => {
        const label = button.textContent;
        button.textContent = button.dataset.copiedLabel || label;
        setTimeout(() => (button.textContent = label), 2000);
      };

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(done, () => this.selectCode());
      } else {
        this.selectCode();
      }
    }

    selectCode() {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-popup-code]'));
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('sos-email-popup', SosEmailPopup);
}
